"use client";

import { useEffect, useRef, useState, type ReactNode } from "react";

/**
 * Defers rendering of heavy below-the-fold content until it's about to enter
 * the viewport (IntersectionObserver). Keeps initial load light — the section
 * only mounts (and pulls its images/iframes/JS) when the user scrolls near.
 * A min-height placeholder preserves scroll position / avoids layout jump.
 */
export function Defer({
  children,
  minHeight = 400,
  rootMargin = "150px",
}: {
  children: ReactNode;
  minHeight?: number | string;
  rootMargin?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    if (shown || !ref.current) return;
    const el = ref.current;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setShown(true);
          io.disconnect();
        }
      },
      { rootMargin },
    );
    io.observe(el);
    // Fallback: ensure the section eventually mounts even if the user jumps
    // past it (anchor / scroll-to-bottom) or never scrolls (crawlers).
    const idle = window.setTimeout(() => setShown(true), 4000);
    return () => {
      io.disconnect();
      window.clearTimeout(idle);
    };
  }, [shown, rootMargin]);

  return <div ref={ref}>{shown ? children : <div style={{ minHeight }} aria-hidden />}</div>;
}
